/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { columns as baseColumns, License } from "./LicensingData";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getLicenseIdGlobal,
  getLicenseListByLicense,
  getLicenseListGlobal,
  updateLicense,
} from "./request/request";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import { ReactComponent as IconEdit } from "../../../shared/assets/Icons/Icon_Edit.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { parseAxiosError } from "../../../shared/components/utils/apiErrorParser";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { setSubscriptionId } from "@redux/slices/tenantSlice";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeLicenseFields } from "./licenseFieldSpec";
import { formatLicenseDate } from "./licenseDate";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
interface LicenseTableItem {
  activated: number;
  description: string;
  enddate: string;
  id: number; // we'll match/fetch details for this license
  serial: string;
  startdate: string;
  subscription: string;
  tenant: number;
  total: number;
  type: string;
}

export interface ExtendedLicenseTable extends LicenseTableItem {
  idLicense?: string;
  cloud?: string;
  region?: string;
  activationDate?: string;
}

// The type we store in licenseDetails[row.id]:
interface LicenseDetail {
  idLicense?: string;
  name?: string;
  description?: string;
  cloud?: string;
  region?: string;
  activationDate?: string;
  // etc., depending on what the second endpoint returns
}
interface ExpandedLicenseItem {
  id?: string;
  name?: string;
  cloud?: string;
  region?: string;
  activated: string;
}

interface LicensingPageProps {
  refreshTrigger?: number; // When this changes, refetch the data
  onEditLicense?: (license: LicenseTableItem) => void; // Callback for edit action
}

function LicensingPage({ refreshTrigger, onEditLicense }: LicensingPageProps = {}) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();

  // Overlay spinner for the entire page
  const [isFetching, setIsFetching] = useState(false);
  const { tenantId } = useAppSelector((state) => state.selection);

  const previousTenantIdRef = useRef<number | undefined>(undefined);

  // Main license list (from FIRST endpoint)
  const [licenseList, setLicenseList] = useState<LicenseTableItem[]>([]);
  const [licenseDescription, setLicenseDescription] = useState<string>("");
  const [selectedLicense, setSelectedLicense] =
    useState<LicenseTableItem | null>(null);
  // This is what we actually render in the table
  const [tablesData, setTablesData] = useState<ExtendedLicenseTable[]>([]);

  // We store subscription data here (from getLicenseIdGlobal)
  const [subscription, setSubscription] = useState<string>("");

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const licenseFields = useMemo(() => makeLicenseFields(), []);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  //Alert
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");

  // A dictionary: key = licenseId, value = array of detail items from second endpoint
  const [licenseDetails, setLicenseDetails] = useState<
    Record<number, LicenseDetail[]>
  >({});
  
  // Estado para tracking de carga de detalles por fila
  const [loadingDetailsFor, setLoadingDetailsFor] = useState<Set<number>>(new Set());

  const fetchLicenseId = async () => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const response = await getLicenseIdGlobal("list", -1, tenantId, token);
      const subscriptionData: string = response.data || "";
      setSubscription(subscriptionData);
      // Store subscription ID in Redux for use by other components
      if (subscriptionData) {
        dispatch(setSubscriptionId(subscriptionData));
      }
    } catch (err) {
      console.error("Error fetching License subscription:", err);
    } finally {
      setIsFetching(false);
    }
  };

  // 2) Fetch base license list (first endpoint)
  const fetchLicenseList = async () => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();

      const response = await getLicenseListGlobal("1", tenantId, token);
      const dataArray: LicenseTableItem[] = response.data || [];

      // Format the date fields
      const formattedData = dataArray.map((item) => ({
        ...item,
        startdate: formatLicenseDate(item.startdate),
        enddate: formatLicenseDate(item.enddate),
      }));

      // Store them in your table
      setLicenseList(formattedData);
      setTablesData(formattedData);
    } catch (err) {
      console.error("Error fetching License list:", err);
    } finally {
      setIsFetching(false);
    }
  };

  // Initial load when component mounts or auth state changes
  useEffect(() => {
    if (isLoading || !isAuthenticated || !tenantId) return;

    const hasTenantChanged =
      previousTenantIdRef.current !== undefined &&
      previousTenantIdRef.current !== tenantId;
    if (hasTenantChanged) {
      // Clear previous data when tenant changes
      setLicenseList([]);
      setTablesData([]);
      setLicenseDetails({});
      setSubscription("");
    }
    previousTenantIdRef.current = tenantId;
    fetchLicenseId();
    fetchLicenseList();
  }, [isLoading, isAuthenticated, tenantId, getAccessTokenSilently, refreshTrigger]);

  const fetchLicenseDetails = async (row: ExtendedLicenseTable) => {
    try {
      setLoadingDetailsFor((prev) => new Set(prev).add(row.id));
      const token = await getAccessTokenSilently();
      // Adjust parameters if your endpoint needs them differently
      const response = await getLicenseListByLicense(
        -1,
        tenantId,
        row.id,
        token
      );
      const licenseIdData = response.data || [];

      // Mapear correctamente según la respuesta del API
      const shapedData: LicenseDetail[] = licenseIdData.map(
        (item: any) => ({
          idLicense: item.id || "",
          name: item.name || "",
          description: item.description || "",
          cloud: item.cloud || "",
          region: item.region || "",
          activationDate: item.activated ? formatLicenseDate(item.activated) : "",
        })
      );

      setLicenseDetails((prev) => ({
        ...prev,
        [row.id]: shapedData,
      }));
    } catch (error) {
      console.error("Error fetching second endpoint data:", error);
    } finally {
      setLoadingDetailsFor((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  // Handler: Edit License
  const handleEditLicense = (row: LicenseTableItem) => {
    // If onEditLicense callback is provided (from AdminCenterLicensingPage), use it
    if (onEditLicense) {
      onEditLicense(row);
      return;
    }
    // Otherwise, use the default edit modal
    setSelectedLicense(row);
    setLicenseDescription(row.description);
    setIsModalOpen(true);
  };
  const handleSaveDescription = async () => {
    if (!selectedLicense) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const response = await updateLicense(
        selectedLicense.id,
        -1,
        tenantId,
        token,
        licenseDescription
      );

      // Check if response contains an error field (even with HTTP 200)
      if (
        response.data &&
        typeof response.data === "object" &&
        "error" in response.data
      ) {
        const errorMessage =
          typeof response.data.error === "string"
            ? response.data.error
            : "An error occurred while updating the license.";

        setAlertVariant("error");
        setAlertTitle("Update Failed!");
        setAlertDescription(errorMessage);
        setIsAlertOpen(true);
        return;
      }
      await fetchLicenseList();
      setAlertVariant("success");
      setAlertTitle("License Updated!");
      setAlertDescription(`License was successfully updated.`);
      setIsAlertOpen(true);
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      const parsedError = parseAxiosError(err);
      setAlertVariant("error");
      setAlertTitle("Update Failed!");
      setAlertDescription(
        parsedError.message ||
          "An error occurred while updating the license. Please try again."
      );
      setIsAlertOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  // Columns with custom Actions
  const finalColumns: GenericTableColumn<ExtendedLicenseTable>[] =
    baseColumns.map((col) => {
      if (col.header === "Actions") {
        return {
          ...col,
          renderCell: (row: ExtendedLicenseTable) => (
            <div className="flex items-center gap-2">
              <button
                className="
                border border-semantic-border-secondary
                rounded
                bg-semantic-bg-primary
                text-semantic-fg-secondary
                hover:bg-semantic-state-active
                w-10 h-10 flex items-center justify-center
              "
                onClick={() => handleEditLicense(row)}
              >
                <IconEdit className="w-6 h-6" />
              </button>
            </div>
          ),
        };
      }
      return col;
    });
  
  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return licenseFields.map((f) => f.label);
  }, [licenseFields]);
    // Build maps for filter normalization (similar to LogsPage and BgpTablesPage)
    const fieldById = useMemo(() => {
      const m = new Map<string, FilterField<ExtendedLicenseTable>>();
      for (const f of licenseFields) {
        const id = (f.key ?? labelId(f.label)).toLowerCase();
        m.set(id, f);
      }
      return m;
    }, [licenseFields]);
  
    const keyMap = useMemo(() => {
      const m = new Map<string, string>();
      for (const f of licenseFields) {
        const id = (f.key ?? labelId(f.label)).toLowerCase();
        const label = f.label.toLowerCase();
        m.set(id, id);
        m.set(label, id);
      }
      return m;
    }, [licenseFields]);
  
    // Normalize filters (similar to LogsPage and BgpTablesPage)
    const normalizedFilters = useMemo(() => {
      return activeFilters.map((f) => {
        // If searchbar provided an op (live typing), keep it
        const token = f.op
          ? { ...f }
          : parseToken(f.key ? `${f.key}=${f.value}` : f.value);
  
        // Map key to canonical id
        if (token.key) {
          const k = token.key.trim().toLowerCase();
          token.key = keyMap.get(k) ?? labelId(token.key);
        }
  
        // If this is a STRING field and user didn't explicitly ask for exact,
        const field = token.key ? fieldById.get(token.key) : undefined;
        if (field?.type === "string") {
          const quoted = token.value.match(/^"(.*)"$/);
          if (quoted) {
            token.value = quoted[1]; // strip quotes
            token.op = "=";
          } else {
            token.op = ":"; // default to contains
          }
        }
        return token;
      });
    }, [activeFilters, keyMap, fieldById]);
  
    // Build predicate using advancedFilters system
    const predicate = useMemo(
      () => buildPredicate<ExtendedLicenseTable>(normalizedFilters, licenseFields),
      [normalizedFilters, licenseFields]
    );
  
    // Apply predicate to filter data
    const filteredData = useMemo(
      () => tablesData.filter(predicate),
      [tablesData, predicate]
    );

  const renderExpandToggle = (
    isExpanded: boolean,
    onToggle: () => void,
    row: ExtendedLicenseTable
  ) => {
    const isLoading = loadingDetailsFor.has(row.id);
    const handleToggle = async () => {
      if (!isExpanded) {
        try {
          await fetchLicenseDetails(row);
          onToggle();
        } catch (error) {
          console.error("Error fetching license details:", error);
        }
      } else {
        onToggle();
      }
    };
    return (
      <GenericButton
        label={isLoading ? "Loading..." : isExpanded ? "Close" : "Devices"}
        variant={isExpanded ? "danger" : "brand"}
        size="sm"
        onClick={handleToggle}
        disabled={isLoading}
        loading={isLoading}
      />
    );
  };

  // Helper component for detail rows
  const DetailRow: React.FC<{ label: string; value: string | undefined }> = ({
    label,
    value,
  }) => (
    <div className="py-1 flex justify-between border-b">
      <span className="text-semantic-text-secondary font-semibold">{label}:</span>
      <span className="text-semantic-text-secondary">{value || "—"}</span>
    </div>
  );

  // Expanded row renderer
  const renderExpanded = (row: ExtendedLicenseTable) => {
    // Pull the second-endpoint data from our dictionary:
    const details = licenseDetails[row.id];
    const isLoading = loadingDetailsFor.has(row.id);
    
    if (!details || details.length === 0) {
      return (
        <div className="p-4">
          <div className="text-semantic-text-secondary">No devices found.</div>
        </div>
      );
    }
      // Configuration for first card
      const firstCardFields: Array<{ label: string; key: keyof LicenseDetail }> = [
        { label: "Name", key: "name" },
        { label: "Description", key: "description" },
        { label: "ID License", key: "idLicense" },
        { label: "Cloud", key: "cloud" },
      ];
  
      // Configuration for second card
      const secondCardFields: Array<{ label: string; key: keyof LicenseDetail }> = [
        { label: "Region", key: "region" },
        { label: "Activated", key: "activationDate" },
      ];
    
      return (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {details.map((item, idx) => (
            <React.Fragment key={idx}>
              <GenericCard
                isCollapsible={false}
                isLoading={isLoading}
                bodyContent={
                  <>
                    {firstCardFields.map((field) => (
                      <DetailRow
                        key={field.key}
                        label={field.label}
                        value={item[field.key] as string | undefined}
                      />
                    ))}
                  </>
                }
              />
              <GenericCard
                isCollapsible={false}
                isLoading={isLoading}
                bodyContent={
                  <>
                    {secondCardFields.map((field) => (
                      <DetailRow
                        key={field.key}
                        label={field.label}
                        value={item[field.key] as string | undefined}
                      />
                    ))}
                  </>
                }
              />
            </React.Fragment>
          ))}
        </div>
      );
  };

  return (
    <div className="relative">
      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              {/* Local Filter */}
              <div className="mb-4 bg-[var(--semantic-bg-primary)]">
                <div className="flex items-end">
                  <div className="flex items-end space-x-4" />
                    <div className="w-full max-w-full sm:max-w-[500px]">
                      <GenericSearchbar
                        placeholder="Find by attribute or value (e.g., License, Type)"
                        suggestedKeys={suggestedKeys}
                        onFiltersChange={setActiveFilters}
                        liveFiltering={true}
                        debounceMs={200}
                      />
                  </div>
                </div>
              </div>

              {/* Table */}
              {isFetching ? (
                  <div className="p-4">
                      <SkeletonTableRows
                          rows={5}
                          cols={5}
                          colWidths={["200px"]}
                          rowHeight={15}
                          rowGapClassName="space-y-3"
                      />
                  </div>
              ) : (
                <GenericTable<ExtendedLicenseTable>
                  columns={finalColumns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  expandable
                  renderExpanded={renderExpanded}
                  renderExpandToggle={renderExpandToggle}
                />
              )}
            </>
          }
        />
      </div>
      <GenericModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={"Edit License"}
        size="lg"
      >
        <div className="space-y-4">
          <label className="mb-1 text-sm font-medium text-semantic-text-primary">
            Description <span className="text-red-500">*</span>
          </label>
          <GenericInput
            placeholder="Description"
            value={licenseDescription}
            onChange={(newDescription: React.SetStateAction<string>) =>
              setLicenseDescription(newDescription)
            }
          />
          <div className="flex items-center w-full justify-center">
            <GenericButton
              label="Save"
              variant="brand"
              size="md"
              onClick={handleSaveDescription}
            />
          </div>
        </div>
      </GenericModal>
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => setIsAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        cancelLabel="Close"
        confirmLabel="OK"
        variant={alertVariant}
      />
    </div>
  );
}

export default LicensingPage;
